import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as core from '../../core'

import { Page } from './page'

export interface AppTemplate
  extends core.common.Identified<AppTemplate.Id>,
    core.admin.App.Input {
  pages: readonly core.admin.App.Page.Input[]
}
export namespace AppTemplate {
  /**
   * Identifies a standard {@link AppTemplate}
   */
  export enum Id {
    Dms = 'dms',
    /** @deprecated - Moved to admin center */
    Quiver = 'quiver',
    MosaicDev = 'mosaic-dev',
  }
  // Needs to be before namespace is defined
  const IDS: ReadonlySet<Id> = new Set(Object.values(Id) as Id[])
  export namespace Id {
    export const ALL: readonly Id[] = Array.from(IDS)
    export const isId = TypeChecker.Factory.oneOf(IDS)
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  type TemplatePageInfo = Replace.Properties<
    Readonly<Omit<core.admin.App.Page.Input, 'display'>>,
    'page',
    { id: Page.Id }
  >
  /**
   * Generates a list of {@link core.admin.App.Page.Input} for an
   * {@link AppTemplate}
   */
  function _getAppPages({
    mainPages = [],
    utilityPages = [],
    hiddenPages = [],
  }: {
    readonly mainPages?: readonly TemplatePageInfo[]
    readonly utilityPages?: readonly TemplatePageInfo[]
    readonly hiddenPages?: readonly TemplatePageInfo[]
  }): readonly core.admin.App.Page.Input[] {
    return [
      { appPages: mainPages, isMain: true },
      { appPages: utilityPages, isMain: false },
      { appPages: hiddenPages },
    ].flatMap(({ isMain, appPages }) =>
      appPages.map<core.admin.App.Page.Input>((appPage, idx) => ({
        // IDs
        ...appPage,
        // If isMain === undefined, it means that page is hidden and it should
        // not be displayed anywhere in the app
        display:
          isMain !== undefined
            ? {
                isMain,
                position: idx,
              }
            : undefined,
      })),
    )
  }
  const LOOKUP: Record<Id, Omit<AppTemplate, 'id'>> = {
    [Id.Dms]: {
      alias: 'dms',
      description: 'View and manage all institutional data and data models.',
      icon: 'dms',
      name: 'DMS',
      pages: _getAppPages({
        hiddenPages: [
          {
            alias: 'dimension',
            name: 'Dimension',
            page: { id: Page.Id.DmsDimension },
          },
          {
            alias: 'entity',
            name: 'Entity',
            page: { id: Page.Id.DmsEntity },
          },
          {
            alias: 'enum',
            name: 'Enum',
            page: { id: Page.Id.DmsEnum },
          },
        ],
        mainPages: [
          {
            alias: 'catalog',
            name: 'Catalog',
            page: { id: Page.Id.DmsCatalog },
          },
          {
            alias: 'graph',
            name: 'Graph',
            page: { id: Page.Id.DmsGraph },
          },
        ],
      }),
      scenarioVariables: [],
      sessionVariables: [],
      supportsScenarios: false,
    },
    [Id.MosaicDev]: {
      alias: 'mosaic',
      description:
        'Develop custom pages or applications through a highly flexible DMS-powered system.',
      icon: 'mosaic',
      name: 'Mosaic',
      pages: _getAppPages({
        hiddenPages: [
          {
            alias: 'page-builder',
            name: 'Page Builder',
            page: { id: Page.Id.MosaicDevPageBuilder },
          },
          {
            alias: 'widget-builder',
            name: 'Widget Builder',
            page: { id: Page.Id.MosaicDevWidgetBuilder },
          },
        ],
        mainPages: [
          {
            alias: 'pages',
            name: 'Pages',
            page: { id: Page.Id.MosaicDevPages },
          },
          {
            alias: 'widgets',
            name: 'Widgets',
            page: { id: Page.Id.MosaicDevWidgets },
          },
          {
            alias: 'actions',
            name: 'Actions',
            page: { id: Page.Id.MosaicDevActions },
          },
        ],
      }),
      scenarioVariables: [],
      sessionVariables: [],
      supportsScenarios: false,
    },
    [Id.Quiver]: {
      alias: 'quiver',
      description: 'Quickly deploy and manage pre-packaged solutions.',
      icon: 'quiver',
      name: 'Quiver',
      pages: _getAppPages({
        mainPages: [
          {
            alias: 'deploy',
            name: 'Deploy',
            page: { id: Page.Id.QuiverDeploymentCenter },
          },
        ],
      }),
      scenarioVariables: [],
      sessionVariables: [],
      supportsScenarios: false,
    },
  }

  /**
   * Retrieves the {@link AppTemplate} for the given {@link AppTemplate.Id}
   */
  export function get(id: Id): AppTemplate {
    return { ...LOOKUP[id], id }
  }
  let _list: AppTemplate[] | undefined
  /**
   * Returns all prebuilt {@link AppTemplate}
   */
  export function list(): readonly AppTemplate[] {
    if (_list) {
      // If we've already created the list before, just return it
      return _list
    }
    // Otherwise, extract the list from the lookup
    _list = Object.entries(LOOKUP)
      .filter(([id]) => id !== Id.Quiver)
      .map<AppTemplate>(([id, appTemplateWithoutId]) => ({
        ...appTemplateWithoutId,
        id: id as keyof typeof LOOKUP,
      }))
    // Sort the entries by name (ensures a consistent sorting that is also
    // user-friendly)
    _list.sort((a, b) => a.name.localeCompare(b.name))

    return _list
  }
}
